import { ChangeDetectionStrategy, Component } from '@angular/core';
import { GridsterModule } from 'angular-gridster2';
import { TsButtonModule, TsColorBubbleComponent, TSIconModule, TsOverlayModule } from 'tsui';
import { Tab, TemplateTab } from '../../types';
import { TsComponent } from '../ts.component';
import { JupiterState } from '../../@store/state';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatLegacyTabsModule } from '@angular/material/legacy-tabs';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { JupiterPanelDef } from '../panels/types';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { filter, map, noop, tap } from 'rxjs';
import { AccountState } from '@state/account/state';
import { AccountColorBackgroundDirective } from '@shared/directives/account-color-background.directive';
import { gridsterConfig, panelLabelMap } from '../../consts';
import { getPanels } from '../../functions';
import { TemplateActions } from '../../@store/actions';
import { LayoutSaveModalComponent } from '../layout-save-modal/layout-save-modal.component';
import { SaveTemplateModalResult } from '@shared/components/widgets/save-template/save-template.types';
import { ofActionCompleted } from '@ngxs/store';

type BlurType = FocusEvent & { target: { innerText: string; contentEditable: boolean | string } };

@Component({
  selector: 'jupiter-layout-manager',
  standalone: true,
  imports: [
    TsButtonModule,
    GridsterModule,
    AsyncPipe,
    MatLegacyTabsModule,
    DragDropModule,
    MatIconModule,
    MatLegacyButtonModule,
    MatLegacyMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatDividerModule,
    AsyncPipe,
    TsButtonModule,
    TSIconModule,
    TsOverlayModule,
    TsColorBubbleComponent,
    AccountColorBackgroundDirective,
    NgClass,
  ],
  templateUrl: './layout-manager.component.html',
  styleUrl: './layout-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterLayoutManagerComponent extends TsComponent {
  tabs = [];
  tabsMemento = [];
  panelLabelMap = panelLabelMap;
  panels: JupiterPanelDef<any>[] = [];
  customObjects$ = this.store
    .select(ObjectsManagerState.getLinkedObjects(this.store.selectSnapshot(JupiterState.getRelatedToObject)))
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((objects) => !!objects),
      map((objects) => {
        return objects.filter((object) => object.id);
      }),
    );
  accountColors$ = this.store.select(AccountState.selectAccountColorsWithNone);
  currentTab = null;
  gridOptions = {
    ...gridsterConfig,
    enableEmptyCellClick: true,
    emptyCellClickCallback: this.emptyCellClick.bind(this),
  };

  private currentPanel = null;
  private defaultPanelLocation = { x: 0, y: 0, cols: 1, rows: 2 };

  constructor() {
    super();

    this.store
      .select(JupiterState.getEntityTabs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tabs) => {
        this.tabs = tabs ?? [];
        this.tabsMemento = JSON.parse(JSON.stringify(this.tabs));
        const [firstTab] = this.tabs;
        this.currentTab = firstTab;
        this.gridOptions?.api?.optionsChanged();
      });

    this.panels = getPanels(this.store.selectSnapshot(JupiterState.getFeatureSupport));
  }

  ngOnInit(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(TemplateActions.Create, TemplateActions.Update),
        tap(({ action }) => {
          if (action instanceof TemplateActions.Update) {
            this.tsSnackbarService.open('Layout updated successfully.');
          } else {
            this.tsSnackbarService.open('Layout created successfully.');
          }
        }),
      )
      .subscribe();
  }

  drop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    this.mutateTabs();
  }

  removeTab(tab: Tab): void {
    if (this.tabs.length === 1) {
      return;
    }

    if (this.currentTab.id === tab.id) {
      this.currentTab = this.tabs[0];
    }
    this.tabs = this.tabs.filter(({ id }) => id !== tab.id);
    this.mutateTabs();
  }

  addRelatedTablePanel(customObject: CustomObject): void {
    const tab = this.tabs[this.getCurrentTabIndex()];
    const tablePanel = {
      ...this.panels.find((panel) => panel.type === 'table'),
      meta: {
        fields: customObject.fields
          .filter((x) => x.name === 'id' || x.id === customObject.search_custom_field_id)
          .map((x) => x.name),
        tableType: customObject.name,
      },
    };

    if (this.currentPanel) {
      const layoutItem = tab.layout.find(({ panel: { id } }) => id === this.currentPanel.id);
      layoutItem.panel = { ...tablePanel };
      this.mutateTabs();
      this.currentPanel = null;
    } else {
      tab.layout = [...tab.layout, { ...this.defaultPanelLocation, panel: { ...tablePanel, id: this.rndId() } }];
      this.mutateTabs();
    }
  }

  addPanel(panelDef: JupiterPanelDef<any>, gridsterLocation = null): void {
    const tab = this.tabs[this.getCurrentTabIndex()];

    if (this.currentPanel) {
      const layoutItem = tab.layout.find(({ panel: { id } }) => id === this.currentPanel.id);
      layoutItem.panel = { id: this.rndId(), ...panelDef };
      this.mutateTabs();
      this.currentPanel = null;
    } else {
      tab.layout = [
        ...tab.layout,
        { ...(gridsterLocation ?? this.defaultPanelLocation), panel: { ...panelDef, id: this.rndId() } },
      ];
      this.mutateTabs();
    }
  }

  onRemovePanel(panel: JupiterPanelDef<any>): void {
    const tabIndex = this.getCurrentTabIndex();
    const tab = this.tabs[tabIndex];
    tab.layout = tab.layout.filter(({ panel: { id } }) => panel.id !== id);
    this.mutateTabs();
  }

  addTab(): void {
    const tabModel: TemplateTab = { name: 'New Tab', layout: [], id: this.rndId() };
    this.tabs = [...this.tabs, { ...tabModel }];
  }

  save(): void {
    const entity = this.store.selectSnapshot(JupiterState.getEntity);

    this.matDialog
      .open(LayoutSaveModalComponent, {
        autoFocus: false,
        data: {
          templateName: entity.template.name,
          typeLabel: this.store.selectSnapshot(
            ObjectsManagerState.getByName(this.store.selectSnapshot(JupiterState.getRelatedTo)),
          ).label,
        },
      })
      .afterClosed()
      .pipe(
        tap((result: SaveTemplateModalResult) => {
          if (result) {
            if (result.saveMode === 'update') {
              this.store.dispatch(
                new TemplateActions.Update({
                  ...entity.template,
                  definition: { ...entity.template.definition, tabs: this.tabs },
                }),
              );
            } else if (result?.saveMode === 'create') {
              const payload = JSON.parse(JSON.stringify(entity.template));
              delete payload.id;
              delete payload.rank;
              payload.name = result.templateName;
              this.store.dispatch(new TemplateActions.Create(payload));
            }
          }
        }),
      )
      .subscribe(noop);
  }

  switchTab(tab: any) {
    this.currentTab = this.tabs.find(({ id }) => id === tab.id);
  }

  selectHeaderColor(color: string): void {
    this.currentPanel.color = color?.toLowerCase() === 'none' ? null : color;
    this.mutateTabs();
  }

  colorMenuOpen(panelDef: JupiterPanelDef<any>): void {
    this.currentPanel = panelDef;
  }

  colorMenuClose(): void {
    this.currentPanel = null;
  }

  emptyCellClick(event: MouseEvent, item: any): void {
    this.addPanel(
      this.panels.find((panel) => panel.type === 'form'),
      item,
    );
  }

  switchPanel(panelDef: JupiterPanelDef<any>): void {
    this.currentPanel = panelDef;
  }

  restore(): void {
    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: 'Restore Layout',
        description: 'Are you sure you want to restore the layout to the last saved version?',
        buttonsConfig: {
          yes: 'Confirm',
          no: 'Cancel',
        },
        styling: {
          yes: 'primary',
        },
      })
      .pipe(
        tap((result) => {
          if (result) {
            this.tabs = JSON.parse(JSON.stringify(this.tabsMemento));
            this.tabsMemento = JSON.parse(JSON.stringify(this.tabs));
            const [firstTab] = this.tabs;
            this.currentTab = firstTab;
            this.gridOptions?.api?.optionsChanged();
            this.tsSnackbarService.open('Layout restored to original version');
          }
        }),
      )
      .subscribe(noop);
  }

  panelTitleBlur({ target }: BlurType, panelDef: JupiterPanelDef<any>): void {
    if (target.innerText?.trim()) {
      target.contentEditable = false;
      const layoutItem = this.tabs[this.getCurrentTabIndex()].layout.find(({ panel: { id } }) => id === panelDef.id);
      layoutItem.panel.title = target.innerText;
      this.mutateTabs();
    }
  }

  tabTitleBlur({ target }: BlurType, tab: TemplateTab): void {
    if (target.innerText?.trim()) {
      target.contentEditable = false;
      const selectedTab = this.tabs[this.tabs.findIndex(({ id }) => id === tab.id)];
      selectedTab.name = target.innerText;
      this.mutateTabs();
    }
  }

  editElement(event: any): void {
    event.target.contentEditable = true;
    window.getSelection().removeAllRanges();
    event.target.focus();
  }

  private getCurrentTabIndex(): number {
    return this.tabs.findIndex(({ id }) => id === this.currentTab.id);
  }

  private mutateTabs(): void {
    this.tabs = [...this.tabs];
  }
}
